// Backtracking — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/heap.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the search.

const backtrackingProblems = [
  {
    id: 'subsets',
    title: 'Subsets',
    difficulty: 'Medium',
    leetcode: { number: 78, url: 'https://leetcode.com/problems/subsets/' },
    statement: 'Given an integer array of unique elements, return every possible subset (the power set) — no duplicate subsets.',
    example: {
      input: 'nums = [1, 2, 3]',
      output: '[[], [1], [2], [1,2], [3], [1,3], [2,3], [1,2,3]]',
      note: 'All 2³ = 8 subsets, including the empty set and the full set.',
    },
    approaches: [
      {
        name: 'Approach 1 — Iterative Bitmask',
        why:
          "Every subset of an n-element array corresponds to exactly one n-bit binary number: bit i set means " +
          "\"include element i,\" bit i clear means \"exclude it.\" Counting from 0 to 2ⁿ − 1 and reading off each " +
          "number's bits generates every subset without any recursion at all.",
        steps: [
          'For every integer mask from 0 to 2ⁿ − 1 (n = array length):',
          'For each bit position i in mask, check whether it\'s set.',
          'If bit i is set, include nums[i] in this subset.',
          'Collect the subset built from each mask — every possible combination of set bits covers every possible subset exactly once.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3] }],
          varNames: ['mask'],
          steps: [
            { vars: { mask: '000' }, note: 'mask = 0 (000) — no bits set. Subset: [].' },
            { vars: { mask: '001' }, highlights: { nums: [0] }, note: 'mask = 1 (001) — bit 0 set. Subset: [1].' },
            { vars: { mask: '011' }, highlights: { nums: [0, 1] }, note: 'mask = 3 (011) — bits 0 and 1 set. Subset: [1, 2].' },
            { vars: { mask: '111' }, highlights: { nums: [0, 1, 2] }, note: 'mask = 7 (111) — all bits set. Subset: [1, 2, 3]. All 8 masks from 0 to 7 cover every subset.' },
          ],
        },
        code:
`List<List<Integer>> subsetsBitmask(int[] nums) {
    int n = nums.length;
    List<List<Integer>> result = new ArrayList<>();
    for (int mask = 0; mask < (1 << n); mask++) {
        List<Integer> subset = new ArrayList<>();
        for (int i = 0; i < n; i++) {
            if ((mask & (1 << i)) != 0) {
                subset.add(nums[i]);
            }
        }
        result.add(subset);
    }
    return result;
}`,
        complexity: { time: 'O(2ⁿ · n)', space: 'O(2ⁿ · n)' },
        whenToUse: "A neat, purely iterative alternative when recursion is undesirable — but it's fundamentally the same 2ⁿ exploration as backtracking, just driven by binary counting instead of a call stack.",
      },
      {
        name: 'Approach 2 — Optimized: Recursive Backtracking',
        why:
          "The direct choose/explore/un-choose template: every partial path built during the recursion is itself " +
          "a complete, valid subset — record it the instant a call begins, then try extending it with each " +
          "remaining candidate.",
        steps: [
          'Record the current path as a subset immediately at the start of every call — it\'s already valid.',
          'For each candidate from the current start index onward, choose it (add to the path).',
          'Recurse from the NEXT index (i + 1) — each element can only be used once.',
          'Un-choose (remove from the path) before trying the next candidate.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3] }, { label: 'path', values: [] }],
          varNames: [],
          steps: [
            { note: 'Empty path recorded immediately: subset [].' },
            { snapshots: { path: [1] }, highlights: { nums: [0] }, note: 'Choose 1. Path [1] recorded as a subset.' },
            { snapshots: { path: [1, 2] }, highlights: { nums: [0, 1] }, note: 'Choose 2 (start index moved past 1). Path [1, 2] recorded.' },
            { snapshots: { path: [1, 2, 3] }, highlights: { nums: [0, 1, 2] }, note: 'Choose 3. Path [1, 2, 3] recorded. Backtrack fully, un-choosing each element, eventually trying every other combination: [1,3], [2], [2,3], [3] — all 8 subsets found.' },
          ],
        },
        code:
`List<List<Integer>> subsets(int[] nums) {
    List<List<Integer>> result = new ArrayList<>();
    backtrack(nums, 0, new ArrayList<>(), result);
    return result;
}

private void backtrack(int[] nums, int start, List<Integer> path, List<List<Integer>> result) {
    result.add(new ArrayList<>(path));
    for (int i = start; i < nums.length; i++) {
        path.add(nums[i]);
        backtrack(nums, i + 1, path, result);
        path.remove(path.size() - 1);
    }
}`,
        complexity: { time: 'O(2ⁿ · n)', space: 'O(n) — recursion depth, versus generating a mask value per subset' },
        interviewNote: "Try the \"Subsets\" mode in this pattern's interactive playground to run this exact algorithm on any small set you choose.",
        whenToUse: "This is the standard answer, and the direct foundation for Subsets II (with duplicates), Combinations, and Combination Sum — the same template with small variations.",
      },
    ],
  },
  {
    id: 'n-queens',
    title: 'N-Queens',
    difficulty: 'Hard',
    leetcode: { number: 51, url: 'https://leetcode.com/problems/n-queens/' },
    statement:
      'Place n queens on an n×n chessboard so that no two queens attack each other (same row, column, or ' +
      'diagonal). Return all distinct board configurations that achieve this.',
    example: {
      input: 'n = 4',
      output: '2 solutions: [".Q..","...Q","Q...","..Q."] and ["..Q.","Q...","...Q",".Q.."]',
      note: 'With one queen forced into a different row each time, exactly 2 arrangements avoid all conflicts.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Place All, Then Validate)',
        why:
          "Try every possible placement of one queen per row, choosing any column freely, and only check the " +
          "ENTIRE board for conflicts once all n queens are placed. Correct, but this builds out enormous numbers " +
          "of boards that were already doomed by a conflict introduced several queens earlier.",
        steps: [
          'Place one queen in each row, trying every column combination.',
          'Only after all n queens are placed, scan the entire board for any conflicting pair.',
          'Keep the configuration if no conflicts exist; discard it otherwise.',
          'A conflict introduced by the 2nd queen still lets queens 3 through n get placed before it\'s ever detected.',
        ],
        code:
`List<List<String>> solveNQueensBruteForce(int n) {
    List<List<String>> result = new ArrayList<>();
    int[] placement = new int[n];
    generateAll(placement, 0, n, result);
    return result;
}

private void generateAll(int[] placement, int row, int n, List<List<String>> result) {
    if (row == n) {
        if (isValidBoard(placement, n)) result.add(buildBoard(placement, n));
        return;
    }
    for (int col = 0; col < n; col++) {
        placement[row] = col;
        generateAll(placement, row + 1, n, result);
    }
}

private boolean isValidBoard(int[] placement, int n) {
    for (int i = 0; i < n; i++) {
        for (int j = i + 1; j < n; j++) {
            if (placement[i] == placement[j] || Math.abs(placement[i] - placement[j]) == j - i) return false;
        }
    }
    return true;
}
// buildBoard(...) converts the column array into the string-row format, omitted for brevity.`,
        complexity: { time: 'O(nⁿ) — every column combination attempted, validated only at the end', space: 'O(n)' },
        whenToUse: "Only useful to confirm what a valid board means — checking after full placement wastes enormous effort on boards that were doomed several queens earlier.",
      },
      {
        name: 'Approach 2 — Optimized: Backtrack With Per-Placement Conflict Checking',
        why:
          "Check each new queen against every ALREADY-PLACED queen the instant it's considered — before recursing " +
          "any further. A conflict is caught the moment it would occur, so a doomed branch never gets the chance " +
          "to place a single additional queen.",
        steps: [
          'Place queens one row at a time.',
          'For each column in the current row, check it against every queen already placed in earlier rows (same column, or same diagonal).',
          'If safe, place the queen there and recurse to the next row; if not, skip this column immediately — no recursion.',
          'When all n rows have a queen placed with no conflicts, record the board. Always remove the queen (backtrack) before trying the next column.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: ['·', '·', '·', '·'] },
            { label: 'row 1', values: ['·', '·', '·', '·'] },
            { label: 'row 2', values: ['·', '·', '·', '·'] },
            { label: 'row 3', values: ['·', '·', '·', '·'] },
          ],
          varNames: [],
          steps: [
            { snapshots: { 'row 0': ['Q', '·', '·', '·'] }, highlights: { 'row 0': [0] }, note: 'Row 0, column 0: no other queens yet — safe. Place.' },
            { snapshots: { 'row 1': ['·', '·', 'Q', '·'] }, highlights: { 'row 1': [2] }, note: 'Row 1: columns 0 and 1 both conflict with row 0\'s queen (same column, or diagonal). Column 2 is safe — place.' },
            { note: 'Row 2: every column conflicts (column, or diagonal, with one of the two placed queens) — dead end. Backtrack row 1, try column 3 instead — that also leads to a dead end at row 2 or 3. Backtrack out of row 0\'s column 0 entirely.' },
            { snapshots: { 'row 0': ['·', 'Q', '·', '·'], 'row 1': ['·', '·', '·', '·'] }, highlights: { 'row 0': [1] }, note: 'Row 0, column 1: place (backtracking has fully reset rows 1-3).' },
            { snapshots: { 'row 1': ['·', '·', '·', 'Q'] }, highlights: { 'row 1': [3] }, note: 'Row 1: column 3 is the only safe option (0-2 all conflict). Place.' },
            { snapshots: { 'row 2': ['Q', '·', '·', '·'] }, highlights: { 'row 2': [0] }, note: 'Row 2: column 0 is safe. Place.' },
            { snapshots: { 'row 3': ['·', '·', 'Q', '·'] }, highlights: { 'row 3': [2] }, note: 'Row 3: column 2 is safe — all 4 rows placed with zero conflicts! Solution found: .Q.. / ...Q / Q... / ..Q. — matching the first expected output exactly.' },
          ],
        },
        code:
`List<List<String>> solveNQueens(int n) {
    List<List<String>> result = new ArrayList<>();
    int[] queens = new int[n];   // queens[row] = column
    backtrack(queens, 0, n, result);
    return result;
}

private void backtrack(int[] queens, int row, int n, List<List<String>> result) {
    if (row == n) {
        result.add(buildBoard(queens, n));
        return;
    }
    for (int col = 0; col < n; col++) {
        if (!isSafe(queens, row, col)) continue;
        queens[row] = col;
        backtrack(queens, row + 1, n, result);
        // no explicit "un-place" needed — queens[row] is simply overwritten next iteration
    }
}

private boolean isSafe(int[] queens, int row, int col) {
    for (int r = 0; r < row; r++) {
        if (queens[r] == col || Math.abs(queens[r] - col) == row - r) return false;
    }
    return true;
}

private List<String> buildBoard(int[] queens, int n) {
    List<String> board = new ArrayList<>();
    for (int col : queens) {
        StringBuilder row = new StringBuilder();
        for (int c = 0; c < n; c++) row.append(c == col ? 'Q' : '.');
        board.add(row.toString());
    }
    return board;
}`,
        complexity: { time: 'Well below O(nⁿ) in practice — conflict-checking prunes the vast majority of branches', space: 'O(n) — the queens array, plus recursion depth' },
        interviewNote: "Try the \"N-Queens\" mode in this pattern's interactive playground to watch the placement, conflict-detection, and backtracking happen live on boards from size 4 to 8.",
        whenToUse: "This is the canonical constraint-satisfaction backtracking problem — checking validity incrementally (per placement) rather than only at the end is the single most important optimization idea in this entire pattern.",
      },
    ],
  },
];

export default backtrackingProblems;
